import {
  useMutation,
  useSuspenseQuery,
  useQueryClient,
} from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createTransactionPin,
  fundWallet,
  getWallet,
  getWalletTransactions,
  transferFundsBtwWallet,
  fundBusinessWallet,
  businesstransferFundsBtwWallet,
  createBusinessPin,
  changeBusinessPin,
  changeClientPin,
  getAllFacilities
} from "service";
import {
  IWalletResponse,
  IFundWalletType,
  IFundWallet,
  ITracsactionHistory,
  IWalletTransferForm,
  IhealthFacilities,
} from "types";
import { useModal } from "./useModal";
import { toast } from "sonner";
import { useHfBranch } from "./hf";

export function useWallet() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { branch } = useHfBranch();
  const location = useLocation();

  const [pagination, setPagination] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 10,
  });

  const [steps, setSteps] = useState(1);
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  const { openModal, closeModal, isOpen } = useModal();
  const {
    openModal: openPinModal,
    closeModal: closePinModal,
    isOpen: isPinModalOpen,
  } = useModal();
  const {
    openModal: openInSufficientModal,
    closeModal: closeInSufficientModal,
    isOpen: isInSufficientModalOpen,
  } = useModal();

  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [isBalanceVisible, setIsBalanceVisible] = useState(() => {
    return localStorage.getItem("isBalanceVisible") !== "false";
  });

  // Save isBalanceVisible to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("isBalanceVisible", isBalanceVisible.toString());
  }, [isBalanceVisible]);

  const [transFormData, setTransFormData] = useState<IWalletTransferForm>({
    pin: "",
    amount: 0,
    recipient_ino_id: "",
  });

  const [formData, setFormData] = useState<IFundWalletType>({
    amount: "",
    payment_gateway: "",
  });

  useEffect(() => {
    const savedPaymentGateway = localStorage.getItem("defaultPaymentGateway");
    if (savedPaymentGateway) {
      setFormData((prev) => ({
        ...prev,
        payment_gateway: savedPaymentGateway,
      }));
      setSaveAsDefault(true);
    }
  }, [setFormData]);
  const { data: walletData, isLoading: walletLoading, error: allWallError } =
    useSuspenseQuery<IWalletResponse>({
      queryKey: ["wallet"],
      queryFn: async () => {
        const response = await getWallet();
        return response;
      },
    });

  const handlePaymentSelect = (optionId: string) => {
    setFormData({ ...formData, payment_gateway: optionId });
    if (saveAsDefault) {
      localStorage.setItem("defaultPaymentGateway", optionId);
    }
  };


  const toggleDefaultOption = () => {
    const newValue = !saveAsDefault;
    setSaveAsDefault(newValue);

    if (newValue && formData.payment_gateway) {
      localStorage.setItem("defaultPaymentGateway", formData.payment_gateway);
    } else if (!newValue) {
      localStorage.removeItem("defaultPaymentGateway");
    }
  };

  const { data: healthFacilities, isLoading: healthFacilitiesIsLoading, error: healthFacilitiesError } =
    useSuspenseQuery<IhealthFacilities>({
      queryKey: ["healthFacilities"],
      queryFn: async () => {
        const response = await getAllFacilities();
        return response;
      },
    });

  const { data: walletTransaction, isLoading: walletTransactionLoading,
    error, refetch } =
    useSuspenseQuery<ITracsactionHistory>({
      queryKey: ["wallet-transaction", pagination.current, pagination.pageSize],
      queryFn: async () => {
        const response = await getWalletTransactions(pagination.current, pagination.pageSize);
        return response;
      },
      staleTime: 0,
    });

  const handlePageChange = (page: number, pageSize?: number) => {
    const newPage = Math.max(1, page);
    setPagination(prev => ({
      current: newPage,
      pageSize: pageSize ?? prev.pageSize,
    }));
    setTimeout(() => refetch(), 0);
  };

  const { mutate: fundWalletMutate, isPending: fundWalletLoading } = useMutation<IFundWallet>({
    mutationFn: async () => {
      const amount = parseFloat(formData.amount);
      if (!formData.amount || isNaN(amount) || amount <= 0 || !/^[0-9]+$/.test(formData.amount)) {
        throw new Error("Please enter a valid amount");
      }
      if (location.pathname.includes("business-wallet")) {
        if (!branch?.hfId || !Number.isInteger(branch.hfId) || branch.hfId <= 0) {
          throw new Error("Invalid business ID");
        }
        return await fundBusinessWallet(branch.hfId, formData.payment_gateway, formData.amount);
      }
      return await fundWallet(formData.payment_gateway, formData.amount);
    },
    onSuccess: async (data) => {

      const externalUrl = data.data.authorization_url;
      if (!externalUrl) {
        toast.error("No authorization URL provided");
        return;
      }
      toast.info("Redirecting to payment gateway...");
      const newWindow = window.open(externalUrl, "_blank");
      if (!newWindow) {
        toast.error("Failed to open payment page. Please allow pop-ups.");
      }
      setFormData({ amount: "", payment_gateway: "" });

      navigate(location.pathname.includes("business-wallet") ? `/${branch?.hfName.toLowerCase()}/business-wallet` : "/wallet");
      await queryClient.invalidateQueries({
        queryKey: ["wallet", "wallet-transaction", "business-wallet", branch?.hfId],
        refetchType: "active"
      });
    },
    onError: (error: any) => {
      let errorMessage = "Failed to fund wallet.";
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data) {
        if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.errors) {
          const errors = error.response.data.errors;
          if (Array.isArray(errors)) {
            errorMessage = errors.map((err: any) => err.detail || err.message).join(", ");
          } else {
            errorMessage = errors;
          }
        }
      }
      toast.error(errorMessage);
    },
  });

  let receivers_wallet;

  const { mutate: transferFundsMutate, isPending: transferFundsLoading } =
    useMutation({
      mutationFn: async () => {
        console.log(healthFacilities);
        console.log(transFormData.recipient_ino_id);
        const hfId = healthFacilities.results.find(r => r.name_of_facility === transFormData.recipient_ino_id)?.id;
        console.log(hfId);

        if (transFormData.recipient_ino_id.includes("INO")) {
          receivers_wallet = "wallet";
        } else {
          receivers_wallet = "business_wallet";
        }
        if (location.pathname.includes("business-wallet")) {
          if (!branch?.hfId || !Number.isInteger(branch.hfId) || branch.hfId <= 0) {
            throw new Error("Invalid business ID");
          }
          if (receivers_wallet === "wallet") {
            return await businesstransferFundsBtwWallet(branch.hfId, receivers_wallet, {
              pin: Number(transFormData.pin),
              amount: Number(transFormData.amount),
              recipient_ino_id: transFormData.recipient_ino_id,
            });
          }
          return await businesstransferFundsBtwWallet(branch.hfId, receivers_wallet, {
            pin: transFormData.pin,
            amount: Number(transFormData.amount),
            healthfacility_id: hfId,
          });
        }
        if (receivers_wallet === "wallet") {
          return await transferFundsBtwWallet(
            receivers_wallet,
            {
              pin: transFormData.pin,
              amount: Number(transFormData.amount),
              recipient_ino_id: transFormData.recipient_ino_id,
            }
          );
        }
        return await transferFundsBtwWallet(
          receivers_wallet,
          {
            pin: transFormData.pin,
            amount: transFormData.amount,
            healthfacility_id: hfId,
          }
        );
      },
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: ["wallet", "wallet-transaction", "business-wallet"],
          refetchType: "active",
        });
        toast.success("Transfer successful");
        navigate(location.pathname.includes("business-wallet") ? `/${branch?.hfName}/business-wallet` : "/wallet");
        setTransFormData({
          pin: "",
          amount: 0,
          recipient_ino_id: "",
        });
        closeModal();
      },
      onError: (error: any) => {
        const errors = error.response?.data.errors;
        if (Array.isArray(errors)) {
          const errorMessages = errors.map((err) => err.detail).join(", ");
          toast.error(errorMessages);
        } else {
          toast.error("An unknown error occurred.");
        }
      },
    });

  const { mutate: createPinMutate, isPending: createPinLoading } = useMutation({
    mutationFn: async () => {
      if (location.pathname.includes("business-wallet")) {
        if (!branch?.hfId || !Number.isInteger(branch.hfId) || branch.hfId <= 0) {
          throw new Error("Invalid business ID");
        }
        return await createBusinessPin(branch.hfId, pin, confirmPin);
      }
      const response = await createTransactionPin(pin, confirmPin);
      return response;
    },
    onSuccess: async (data) => {
      toast.success("Pin creation was successful");
      closePinModal();
    },
    onError: (error: any) => {
      const errors = error.response?.data.errors;
      if (Array.isArray(errors)) {
        const errorMessages = errors.map((err) => err.detail).join(", ");
        toast.error(errorMessages);
      } else {
        toast.error("An unknown error occurred.");
      }
    },
  });

  const { mutate: changePin, isPending: changePinLoading } = useMutation({
    mutationFn: async ({ oldPin, newPin, confirmPin }: { oldPin: string; newPin: string; confirmPin: string }) => {
      if (location.pathname.includes("business-wallet")) {
        if (!branch?.hfId || !Number.isInteger(branch.hfId) || branch.hfId <= 0) {
          throw new Error("Invalid business ID");
        }
        return await changeBusinessPin(branch.hfId, oldPin, newPin, confirmPin);
      }
      return await changeClientPin(oldPin, newPin, confirmPin);
    },
    onSuccess: async () => {
      toast.success("PIN changed successfully");
    },
    onError: (error: any) => {
      const errors = error.response?.data.errors;
      if (Array.isArray(errors)) {
        const errorMessages = errors.map((err) => err.detail).join(", ");
        toast.error(errorMessages);
      } else {
        toast.error("An unknown error occurred.");
      }
    },
  });

  useEffect(() => {
    if (allWallError) {
      toast.error("Failed to fetch wallet");
    }
  });

  const hanldeRefreshWallet = async () => {
    try {
      await queryClient.invalidateQueries({
        queryKey: ["wallet"],
        refetchType: "active",
      });
      toast.success("Wallet refreshed");
    } catch (err) {
      console.error("Failed to refresh wallet", err);
      toast.error("Failed to refresh wallet");
    }
  };

  const handleTransFieldChange = (
    field: keyof IWalletTransferForm,
    value: string | number | boolean
  ) => {
    setTransFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleFundWallet = () => {
    fundWalletMutate();
  };

  const handleTranfer = () => {
    transferFundsMutate();
  };

  const handleCreatePin = () => {
    createPinMutate();
  };

  const currencyFormatter = () => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      currencyDisplay: "narrowSymbol",
    });
  };

  const toggleBalanceVisibility = () => {
    setIsBalanceVisible((prev) => !prev);
  };

  const getFormattedBalance = () => {
    if (!walletData?.balance) return "₦0.00";
    if (!isBalanceVisible) return "****";
    return currencyFormatter().format(Number(walletData.balance));
  };

  const isTransFormComplete = () => {
    return (
      transFormData.amount &&
      transFormData.recipient_ino_id &&
      transFormData.pin
    );
  };

  const isPinFormComplete = () => {
    return pin && confirmPin;
  };

  const loading =
    walletLoading ||
    walletTransactionLoading ||
    fundWalletLoading ||
    transferFundsLoading;

  return {
    walletData,
    walletTransaction,
    isLoading: loading,
    allWallError,
    formData,
    steps,
    transFormData,
    isOpen,
    pin,
    confirmPin,
    createPinLoading,
    isPinModalOpen,
    openPinModal,
    closePinModal,
    setPin,
    setConfirmPin,
    handleCreatePin,
    handleFundWallet,
    setFormData,
    navigate,
    setSteps,
    hanldeRefreshWallet,
    currencyFormatter,
    handleTransFieldChange,
    handleTranfer,
    openModal,
    closeModal,
    isTransFormComplete,
    isPinFormComplete,
    pagination,
    handlePageChange,
    changePin,
    changePinLoading,
    isBalanceVisible,
    toggleBalanceVisibility,
    getFormattedBalance,
    handlePaymentSelect,
    saveAsDefault,
    toggleDefaultOption
  };
}